import { type MouseEvent } from 'react';
import { Helmet } from 'react-helmet';
import { Menu } from '~/modules/Elements/AKUI/Menu';
import MenuWithLogo from '~/modules/Elements/MenuWithLogo';
import SmoothLink from '~/modules/Elements/SmoothLink';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import useSmoothNavigate from '~/modules/hooks/useSmoothNavigate';
import { useSetlist } from '~/modules/Songs/hooks/useSetlist';

type MouseFocusableProps = {
  onMouseEnter?: (e: MouseEvent) => void;
};

function ManageSongs() {
  const navigate = useSmoothNavigate();
  const goBack = () => navigate('');
  const setlist = useSetlist();

  const { register, focusElement } = useKeyboardNav({ onBackspace: goBack });

  const withMouseFocus = <T extends object>(name: string, props: T): T & MouseFocusableProps => {
    const currentOnMouseEnter = (props as MouseFocusableProps).onMouseEnter;
    const onMouseEnter = (e: MouseEvent) => {
      focusElement(name, 'mouse');
      currentOnMouseEnter?.(e);
    };
    return { ...props, onMouseEnter };
  };

  return (
    <MenuWithLogo
      showLogo={false}
      showGithubRibbon={false}
      supportedBrowsers={false}
      backgroundImage="/menu_background_blur.png"
      backgroundOverlay={true}
      toolbar={true}>
      <Helmet>
        <title>Manage Songs | fastKaraoke</title>
      </Helmet>

      <h1 className="mb-2 text-center text-2xl font-semibold tracking-wide text-white/90">Manage Songs</h1>

      <SmoothLink to="exclude-languages/">
        <Menu.Button
          {...withMouseFocus(
            'exclude-languages',
            register('exclude-languages', () => navigate('exclude-languages/'), undefined, true),
          )}>
          Select Song Languages
        </Menu.Button>
      </SmoothLink>

      {setlist.isEditable && (
        <>
          <SmoothLink to="edit/list/">
            <Menu.Button
              {...withMouseFocus(
                'edit-songs',
                register('edit-songs', () => navigate('edit/list/')),
              )}>
              Edit songs
            </Menu.Button>
          </SmoothLink>

          <SmoothLink to="edit/setlists">
            <Menu.Button
              {...withMouseFocus(
                'edit-setlists',
                register('edit-setlists', () => navigate('edit/setlists')),
              )}>
              Manage setlists
            </Menu.Button>
          </SmoothLink>

          <SmoothLink to="convert/">
            <Menu.Button
              {...withMouseFocus(
                'convert-song',
                register('convert-song', () => navigate('convert/')),
              )}>
              Convert UltraStar .txt
            </Menu.Button>
          </SmoothLink>
        </>
      )}

      <hr className="border-white/20" />

      <SmoothLink to="">
        <Menu.Button {...withMouseFocus('back-button', register('back-button', goBack))}>
          Return To Song List
        </Menu.Button>
      </SmoothLink>
    </MenuWithLogo>
  );
}

export default ManageSongs;
